import type { SwingConfig, SwingSector, SwingSkipReason, SwingTarget } from './swingTypes';

/**
 * 국내 스윙 화면 표시 규칙. (2026-10-10 · docs/impl/141 12.6-5)
 *
 * 감시 대상 표 — 사용자 요청(10/10): 섹터 필터 "모두"(기본) + 다섯 섹터, 그리고 "왕복성 순위" · "매수 순서(예상)" 두 보기를
 * **함께** 걸 수 있어야 한다. 섹터를 고르면 머리에 "보유 n/4", 행마다 못 사는 이유.
 *
 * 🔴 판정은 서버(엔진)가 한다 — 진입선(재진입 반영) · 못 산 이유는 응답 값을 그대로 쓴다. 화면이 엔진 판정을 다시 짜지 않는다
 * 🔴 저점매수의 "손절선 아래면 안 산다" 는 스윙에 없다 — bandDisplay.targetBuyState 를 쓰지 않는 이유
 */

/** 섹터 필터 — 'ALL' 이면 모두 */
export const ALL_SECTORS = 'ALL';

export function filterBySector(targets: SwingTarget[], sector: string): SwingTarget[] {
  return sector === ALL_SECTORS ? targets : targets.filter((t) => t.sector === sector);
}

/** 섹터를 골랐을 때 머리 문구 — "보유 2/4". 그 섹터 줄이 없으면 null */
export function sectorHoldingLabel(sectors: SwingSector[], sector: string): string | null {
  const row = sectors.find((s) => s.name === sector);
  return row ? `보유 ${row.holding}/${row.cap}` : null;
}

/**
 * 진입선 대비 거리(%) — 살아 있는 현재가가 있으면 그것으로 다시 잰다(bandDisplay.targetDistancePercent 와 같은 이유).
 * @returns 양수면 진입선 위, 음수 · 0 이면 매수 구간. 현재가를 모르면 서버 값
 */
export function swingDistancePercent(target: SwingTarget, livePrice: number | null): number | null {
  const current = livePrice ?? target.currentPrice;
  if (current === null || target.bandPrice === 0) {
    return target.distancePercent;
  }
  return ((current - target.bandPrice) / target.bandPrice) * 100;
}

/**
 * 한 종목의 지금 상태.
 *   held     이미 들고 있다(추가 단은 보유 표의 사다리가 따로 본다)
 *   blocked  진입선 아래인데 엔진이 오늘 못 샀다(이유는 skipReason)
 *   buy      진입선 아래 · 막힌 기록 없음 — 다음 감시에 산다(예수금 · 자리가 있으면)
 *   watch    아직 진입선 위
 */
export type SwingBuyState = 'held' | 'blocked' | 'buy' | 'watch';

export function swingBuyState(target: SwingTarget, livePrice: number | null, held: boolean): SwingBuyState {
  if (held) {
    return 'held';
  }
  const distance = swingDistancePercent(target, livePrice);
  if (distance === null || distance > 0) {
    return 'watch';
  }
  return target.skipReason !== null ? 'blocked' : 'buy';
}

/** 왕복성 순위(풀 순서) — 기본 보기 */
export function sortByRank(targets: SwingTarget[]): SwingTarget[] {
  return [...targets].sort((a, b) => a.swingRank - b.swingRank);
}

/**
 * 매수 순서(예상) — 엔진이 사는 순서를 그대로 옮긴다(bandDisplay.sortByBuyOrder 와 같은 생각).
 *   ① 지금 살 것(buy) — 풀 순서 ② 아직 위(watch) — 진입선에 가까운 것부터(거리를 모르면 그 뒤)
 *   ③ 못 사는 것(blocked) · 보유 중(held) — 풀 순서
 */
export function sortByBuyOrder(
  targets: SwingTarget[],
  livePriceOf: (stockId: number) => number | null,
  heldIds: ReadonlySet<number>,
): SwingTarget[] {
  const group = (s: SwingBuyState) => (s === 'buy' ? 0 : s === 'watch' ? 1 : 2);
  return targets
    .map((target) => {
      const live = livePriceOf(target.stockId);
      return {
        target,
        group: group(swingBuyState(target, live, heldIds.has(target.stockId))),
        distance: swingDistancePercent(target, live),
      };
    })
    .sort((a, b) => {
      if (a.group !== b.group) {
        return a.group - b.group;
      }
      if (a.group === 1) {
        if (a.distance === null || b.distance === null) {
          return a.distance === null ? (b.distance === null ? 0 : 1) : -1;
        }
        if (a.distance !== b.distance) {
          return a.distance - b.distance;
        }
      }
      return a.target.swingRank - b.target.swingRank;
    })
    .map((row) => row.target);
}

/**
 * 엔진이 남긴 못 산 이유를 사람이 읽는 말로. 숫자는 설정(config)에서 넣는다 — 화면에 박지 않는다.
 */
export function skipReasonLabel(reason: SwingSkipReason, config: SwingConfig): string {
  switch (reason) {
    case 'SECTOR_CAP':
      return `섹터 ${config.sectorHold}종목 참`;
    case 'HOLD_CAP':
      return `보유 ${config.maxPositions}종목 참`;
    case 'CASH':
      return '예수금 부족';
    case 'RESERVED':
      return '다른 종목 추가단 몫으로 남겨 둠';
    case 'OVER_CAP':
      return `1주가 종목 한도(${Math.round(config.stockCap / 10_000).toLocaleString('ko-KR')}만원)를 넘음`;
    case 'REENTRY':
      return '손절 뒤 재진입선 위';
    case 'SAME_DAY':
      return '오늘 판 종목';
  }
}

/** 보유 표의 매도 사유 · 체결 사유를 짧은 말로 (백엔드 AccountSlackBlocks.swingReasonLabel 과 같은 말) */
export function swingReasonLabel(reason: string | null, legNo = 0): string {
  switch (reason) {
    case 'LADDER':
      return `익절 ${legNo}단`;
    case 'LADDER_END':
      return '익절 끝';
    case 'STOP':
      return '손절';
    case 'BREAKEVEN':
      return '본전';
    case 'EXPIRE':
      return '만료';
    case 'ROUND_END':
      return '회차 종료';
    default:
      return reason ?? '-';
  }
}
